import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { LocalDateSchema } from './nutrition.js';

// Per-day adherence row. One per ISO weekday in the review window. dayMet
// requires BOTH tolerances to hold; the validator threshold matches
// ADR-0012's ±15% kcal / ±15g protein band so "on plan" means the same
// thing across diet generation and weekly scoring (ADR-0017).
export const WeeklyReviewPerDaySchema = z.object({
  date: LocalDateSchema,
  targetKcal: z.number().min(0),
  actualKcal: z.number().min(0),
  targetProteinG: z.number().min(0),
  actualProteinG: z.number().min(0),
  // Carbs/fat actuals power the Week macro-average card. Added after launch;
  // optional so pre-existing persisted review rows (which lacked these) still
  // parse — consumers read them as `?? 0`. Not part of dayMet — adherence
  // stays kcal + protein (ADR-0017).
  actualCarbsG: z.number().min(0).optional(),
  actualFatG: z.number().min(0).optional(),
  kcalWithinTolerance: z.boolean(),
  proteinWithinTolerance: z.boolean(),
  dayMet: z.boolean(),
});
export type WeeklyReviewPerDay = z.infer<typeof WeeklyReviewPerDaySchema>;

export const WeeklyReviewAdherenceSchema = z.object({
  daysMet: z.number().int().min(0).max(7),
  daysLogged: z.number().int().min(0).max(7),
  perDay: z.array(WeeklyReviewPerDaySchema).length(7),
});
export type WeeklyReviewAdherence = z.infer<typeof WeeklyReviewAdherenceSchema>;

// Weight trend over the review window. Null when the user logged no weights
// during the week — M6.1 does not backfill from prior weeks. dataPoints is
// ordered by date ascending so the UI can plot directly without sorting.
export const WeeklyReviewWeightTrendSchema = z.object({
  startKg: z.number().min(20).max(300),
  endKg: z.number().min(20).max(300),
  deltaKg: z.number(),
  dataPoints: z.array(
    z.object({
      date: LocalDateSchema,
      weightKg: z.number().min(20).max(300),
    }),
  ),
});
export type WeeklyReviewWeightTrend = z.infer<typeof WeeklyReviewWeightTrendSchema>;

// ADR-0017 versioned content shape. M6.1 ships v1 with diagnosis + proposal
// nulled out; M6.2 (ADR-0018) adds the AI diagnosis/proposal fields under v2
// with a converter from v1 (the v1 rows persist as-is and are converted on
// read).
export const WeeklyReviewContentV1Schema = z.object({
  schemaVersion: z.literal('v1'),
  weekStart: IsoDateSchema,
  weekEnd: IsoDateSchema,
  adherence: WeeklyReviewAdherenceSchema,
  weightTrend: WeeklyReviewWeightTrendSchema.nullable(),
  diagnosis: z.null(),
  proposal: z.null(),
});
export type WeeklyReviewContentV1 = z.infer<typeof WeeklyReviewContentV1Schema>;

// ADR-0018 proposal vocabulary. Three primitives, discriminated on `type`.
// Server validates against this schema after the LLM call and treats any
// other shape as a parse failure (one repair retry, then 422).
//
// slotKey enum mirrors the SLOT_ORDER constant in
// apps/api/src/modules/ai/diet/diet.service.ts (note `snacks` plural, not
// `snack` — `MealType` enum value).
export const WeeklyReviewSlotKeySchema = z.enum(['breakfast', 'lunch', 'snacks', 'dinner']);
export type WeeklyReviewSlotKey = z.infer<typeof WeeklyReviewSlotKeySchema>;

export const WeeklyReviewProposalSwapFoodSchema = z.object({
  type: z.literal('swap_food'),
  fromFoodId: IdSchema,
  toFoodId: IdSchema,
  slotKey: WeeklyReviewSlotKeySchema,
  reasoning: z.string().min(1).max(200),
});
export type WeeklyReviewProposalSwapFood = z.infer<typeof WeeklyReviewProposalSwapFoodSchema>;

export const WeeklyReviewProposalAdjustSlotMacrosSchema = z.object({
  type: z.literal('adjust_slot_macros'),
  slotKey: WeeklyReviewSlotKeySchema,
  kcalDelta: z.number().int().min(-500).max(500),
  proteinDeltaG: z.number().int().min(-50).max(50),
  reasoning: z.string().min(1).max(200),
});
export type WeeklyReviewProposalAdjustSlotMacros = z.infer<
  typeof WeeklyReviewProposalAdjustSlotMacrosSchema
>;

export const WeeklyReviewProposalAdjustDailyMacrosSchema = z.object({
  type: z.literal('adjust_daily_macros'),
  kcalDelta: z.number().int().min(-500).max(500),
  proteinDeltaG: z.number().int().min(-50).max(50),
  reasoning: z.string().min(1).max(200),
});
export type WeeklyReviewProposalAdjustDailyMacros = z.infer<
  typeof WeeklyReviewProposalAdjustDailyMacrosSchema
>;

export const WeeklyReviewProposalSchema = z.discriminatedUnion('type', [
  WeeklyReviewProposalSwapFoodSchema,
  WeeklyReviewProposalAdjustSlotMacrosSchema,
  WeeklyReviewProposalAdjustDailyMacrosSchema,
]);
export type WeeklyReviewProposal = z.infer<typeof WeeklyReviewProposalSchema>;

/**
 * A proposal is a no-op when it cannot move the user's plan. The Sonnet
 * weekly diagnosis occasionally emits these on weeks where the right
 * answer is "no change" — `adjust_*_macros` with both deltas at 0, or a
 * `swap_food` whose from/to ids are identical. Both the server-side
 * diagnosis pipeline (before persisting) and the /review render gate
 * (defense for rows persisted before this helper existed) call this to
 * collapse the proposal to null so the UI never paints an empty card.
 */
export function isNoOpProposal(p: WeeklyReviewProposal): boolean {
  switch (p.type) {
    case 'adjust_slot_macros':
    case 'adjust_daily_macros':
      return p.kcalDelta === 0 && p.proteinDeltaG === 0;
    case 'swap_food':
      return p.fromFoodId === p.toFoodId;
  }
}

export const WeeklyReviewDecisionSchema = z.enum(['accepted', 'rejected', 'dismissed']);
export type WeeklyReviewDecision = z.infer<typeof WeeklyReviewDecisionSchema>;

// ADR-0018 V2 content. Adds diagnosis/proposal/decision/generatedBy on top
// of the V1 fields. `diagnosis` has no min length so the V1→V2 converter
// can emit an empty string for legacy rows that pre-date the AI loop;
// production write paths always populate it (canned text or Sonnet output).
export const WeeklyReviewContentV2Schema = z.object({
  schemaVersion: z.literal('v2'),
  weekStart: IsoDateSchema,
  weekEnd: IsoDateSchema,
  adherence: WeeklyReviewAdherenceSchema,
  weightTrend: WeeklyReviewWeightTrendSchema.nullable(),
  diagnosis: z.string().max(800),
  proposal: WeeklyReviewProposalSchema.nullable(),
  decision: WeeklyReviewDecisionSchema.nullable(),
  generatedBy: z.enum(['ai', 'canned']),
});
export type WeeklyReviewContentV2 = z.infer<typeof WeeklyReviewContentV2Schema>;

export const WeeklyReviewContentSchema = z.discriminatedUnion('schemaVersion', [
  WeeklyReviewContentV1Schema,
  WeeklyReviewContentV2Schema,
]);
export type WeeklyReviewContent = z.infer<typeof WeeklyReviewContentSchema>;

/**
 * Converts a stored V1 content row into the V2 shape. Used on read so
 * clients only ever see V2 — V1 rows in the database are preserved as-is
 * (write-once history per ADR-0017). Sets diagnosis="", proposal=null,
 * decision=null, generatedBy="canned" — the canned tag is a truthful
 * signal that no AI call was involved.
 */
export function convertV1ToV2(v1: WeeklyReviewContentV1): WeeklyReviewContentV2 {
  return {
    schemaVersion: 'v2',
    weekStart: v1.weekStart,
    weekEnd: v1.weekEnd,
    adherence: v1.adherence,
    weightTrend: v1.weightTrend,
    diagnosis: '',
    proposal: null,
    decision: null,
    generatedBy: 'canned',
  };
}

// Section 2 projection (review V2 spec §5.2 + §6). Recomputed live by
// the API on every GET /v1/reviews/current — never persisted alongside
// the WeeklyReview row, since it is a pure function of (allWeightLogs,
// profile.goal, now) and must update freely as the user logs new weights.
// The calc emits the discriminant; the web emits the sentence.
export const ProjectionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('on-track'),
    projectedWeightKg: z.number(),
    daysAhead: z.number().int().min(0),
    targetWeightKg: z.number(),
    targetDate: IsoDateSchema,
  }),
  z.object({
    kind: z.literal('behind'),
    projectedWeightKg: z.number(),
    daysBehind: z.number().int().min(1),
    targetWeightKg: z.number(),
    targetDate: IsoDateSchema,
  }),
  z.object({ kind: z.literal('wrong-direction'), slopeKgPerDay: z.number() }),
  z.object({ kind: z.literal('flat'), slopeKgPerDay: z.number() }),
  z.object({ kind: z.literal('insufficient-data') }),
  z.object({ kind: z.literal('goal-passed') }),
  z.object({ kind: z.literal('no-goal') }),
]);
export type Projection = z.infer<typeof ProjectionSchema>;

export const WeeklyReviewSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  weekStart: IsoDateSchema,
  weekEnd: IsoDateSchema,
  content: WeeklyReviewContentSchema,
  createdAt: IsoDateSchema,
});
export type WeeklyReview = z.infer<typeof WeeklyReviewSchema>;

// GET /v1/reviews/current response. id + createdAt are nullable because the
// endpoint computes-on-the-fly when no row exists yet for the current week
// (M6.1 carve-out preserved). When a row exists, id + createdAt are
// populated and content is the stored row converted to V2 if needed.
export const GetCurrentReviewResponseSchema = z.object({
  review: z.object({
    id: IdSchema.nullable(),
    userId: IdSchema,
    weekStart: IsoDateSchema,
    weekEnd: IsoDateSchema,
    content: WeeklyReviewContentSchema,
    createdAt: IsoDateSchema.nullable(),
  }),
  // Section 2 projection — always present (never null; the discriminated
  // union covers `no-goal` and `insufficient-data` explicitly).
  projection: ProjectionSchema,
  // Deterministic whole-week verdict. AI explains this evidence but never
  // decides the status shown in the UI.
  status: z.enum(['on-track', 'drifting', 'off-track', 'neutral']),
});
export type GetCurrentReviewResponse = z.infer<typeof GetCurrentReviewResponseSchema>;

// POST /v1/reviews/run response — always returns a persisted row.
export const RunReviewResponseSchema = z.object({
  review: WeeklyReviewSchema,
});
export type RunReviewResponse = z.infer<typeof RunReviewResponseSchema>;

// POST /v1/reviews/:id/accept response — returns the updated review row +
// the id of the diet plan that was mutated in place (ADR-0016).
export const AcceptReviewResponseSchema = z.object({
  review: WeeklyReviewSchema,
  /**
   * Id of whatever the proposal mutated — the diet Plan, or the superseding
   * NutritionTarget when the user tracks against a deterministic band.
   * Read `appliedTo` before assuming which.
   */
  updatedPlanId: IdSchema,
  /**
   * Which surface absorbed the change. Defaults to `diet_plan` so rows
   * persisted before tracker-first accepts existed still parse.
   */
  appliedTo: z.enum(['diet_plan', 'nutrition_target']).default('diet_plan'),
});
export type AcceptReviewResponse = z.infer<typeof AcceptReviewResponseSchema>;

// POST /v1/reviews/:id/reject and POST /v1/reviews/:id/dismiss share this
// shape — neither mutates the diet plan.
export const RejectReviewResponseSchema = z.object({
  review: WeeklyReviewSchema,
});
export type RejectReviewResponse = z.infer<typeof RejectReviewResponseSchema>;
